import { forwardRef, useId, useImperativeHandle, useRef, useState } from 'react';
import {
  CellApiModule,
  CellStyleModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ColumnApiModule,
  CustomEditorModule,
  EventApiModule,
  ModuleRegistry,
  RenderApiModule,
  RowApiModule,
  RowDragModule,
  SelectEditorModule,
  TextEditorModule,
  TooltipModule,
  themeQuartz,
  type ColDef,
  type IRowNode,
  type RefreshCellsParams,
  type ValueSetterParams,
} from 'ag-grid-community';
import type { CustomCellEditorProps } from 'ag-grid-react';
import type { ItemResponse, LinhaSimuladaResponse } from '@triade/contracts';
import { MODULOS_VALIDACAO } from '@/lib/ag-grid-validacao';
import { cn } from '@/lib/utils';
import type { CentroOpcao, CampoEdicao, LinhaFicha, TipoComponente } from './tipos';

// So modulos Community, cada um explicito (nada de AllCommunityModule). ValidationModule avisa no console, fora de producao,
// se faltar um modulo para uma opcao usada.
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ClientSideRowModelApiModule,
  RowApiModule,
  RenderApiModule,
  CellApiModule,
  ColumnApiModule,
  EventApiModule,
  RowDragModule,
  TextEditorModule,
  SelectEditorModule,
  CellStyleModule,
  TooltipModule,
  CustomEditorModule,
  ...MODULOS_VALIDACAO,
]);

/** Tema Quartz com os tokens do design (CSS variables de globals.css) e Geist. */
export const TEMA = themeQuartz.withParams({
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  headerFontSize: 12,
  headerFontWeight: 500,
  headerTextColor: 'var(--ink-muted)',
  headerBackgroundColor: 'var(--surface-muted)',
  backgroundColor: 'var(--card)',
  foregroundColor: 'var(--ink-secondary)',
  borderColor: 'var(--border)',
  accentColor: 'var(--primary)',
  rowHoverColor: 'var(--surface-muted)',
  selectedRowBackgroundColor: 'var(--surface-muted)',
  rowHeight: 42,
  headerHeight: 36,
  wrapperBorder: false,
  wrapperBorderRadius: 0,
  cellHorizontalPadding: 10,
  inputFocusBorder: { color: 'var(--primary)' },
  inputFocusShadow: { color: 'rgba(37,99,235,0.12)', spread: 3, radius: 0 },
});

/** Contexto que as celulas leem (a grade nao guarda estado de dominio): resultado da API, permissoes e acoes. */
export interface ContextoGrade {
  editavel: boolean;
  editar: (uid: string, campo: CampoEdicao, valor: string) => void;
  remover: (uid: string) => void;
  /** Todos os analiticos (mostrar o centro ja gravado, mesmo que nao aceite etapa). */
  centros: ReadonlyArray<CentroOpcao>;
  /** Opcoes do editor de centro, quando o conjunto e menor que `centros` (etapa: so centros que aceitam etapa). */
  opcoesCentro?: ReadonlyArray<CentroOpcao>;
  /** Resultado da simulacao (API) por uid; ausente antes da primeira resposta. */
  calculo?: ReadonlyMap<string, LinhaSimuladaResponse>;
  /** Tipos que a secao aceita (materiais). */
  tipos?: TipoComponente[];
  /** Fornecedores da empresa (custo.ver): opcoes da coluna Fornecedor de material comprado. */
  fornecedores?: ReadonlyArray<{ id: string; nome: string }>;
  /** Troca o item componente de uma linha de material (editor da coluna Componente). */
  escolherItem?: (uid: string, item: ItemResponse) => void;
  /** Item dono da ficha (nao pode ser componente dele mesmo: some da busca). */
  itemIdDaFicha?: string;
  /** Colunas a marcar em vermelho por linha (uid): o que a validacao ao salvar apontou (Sessao 37). */
  erros?: ReadonlyMap<string, ReadonlySet<string>>;
}

export const ctx = (p: { context: unknown }) => p.context as ContextoGrade;

/** Classes da celula marcada pela validacao ao salvar (fundo e texto de erro). */
export const CLASSE_ERRO = '!bg-danger-bg !text-danger-ink';

/** Acrescenta a cada coluna a regra que pinta a celula quando a validacao ao salvar a apontou para aquela linha. */
export function comMarcaDeErro<T extends ColDef<LinhaFicha>>(defs: T[]): T[] {
  return defs.map((c) =>
    c.colId
      ? { ...c, cellClassRules: { ...(c.cellClassRules ?? {}), [CLASSE_ERRO]: (p: { data?: LinhaFicha; context: unknown }) => !!p.data && !!ctx(p).erros?.get(p.data.uid)?.has(c.colId as string) } }
      : c,
  );
}

/** Setter de uma coluna: o dado vive no estado da tela; a grade se redesenha quando as linhas mudam. */
export function setter(campo: CampoEdicao) {
  return (p: ValueSetterParams<LinhaFicha>) => {
    ctx(p).editar(p.data.uid, campo, p.newValue == null ? '' : String(p.newValue));
    return false;
  };
}

/** Alinhamento e fonte tabular de colunas de numero e R$. */
export const DIREITA = { headerClass: 'ag-right-aligned-header', cellClass: 'ag-right-aligned-cell tabular' } as const;

export const rotuloCentro = (c: CentroOpcao) => `${c.caminho} · ${c.nome}`;

/** Acha o centro digitado: pelo rotulo "caminho · nome", pelo caminho ("3.1") ou pelo codigo. */
export function acharCentro(centros: ReadonlyArray<CentroOpcao>, texto: string): CentroOpcao | undefined {
  const x = texto.trim().toLowerCase();
  if (!x) return undefined;
  return (
    centros.find((c) => rotuloCentro(c).toLowerCase() === x) ??
    centros.find((c) => c.caminho.toLowerCase() === x) ??
    centros.find((c) => c.codigo.toLowerCase() === x)
  );
}

interface RefEditorCentro {
  afterGuiAttached: () => void;
}

/**
 * Combobox de CENTRO ANALITICO (so analiticos chegam aqui): campo com lista de busca (datalist) onde cada opcao mostra
 * "caminho · nome". Digitacao que casa com um centro avisa a grade; texto que nao casa mantem o ultimo centro valido;
 * vazio tira o centro (quando a coluna permite).
 */
export const EditorCentro = forwardRef<RefEditorCentro, CustomCellEditorProps<LinhaFicha>>(function EditorCentro(props, ref) {
  const centros = ctx(props).opcoesCentro ?? ctx(props).centros;
  const lista = useId();
  const campo = useRef<HTMLInputElement>(null);
  const [texto, setTexto] = useState(() => {
    const atual = centros.find((c) => c.id === props.value);
    return atual ? rotuloCentro(atual) : '';
  });
  useImperativeHandle(ref, () => ({
    afterGuiAttached: () => {
      campo.current?.focus();
      campo.current?.select();
    },
  }));
  const aoDigitar = (t: string) => {
    setTexto(t);
    if (t.trim() === '') props.onValueChange(null);
    else {
      const c = acharCentro(centros, t);
      if (c) props.onValueChange(c.id);
    }
  };
  return (
    <>
      <input
        ref={campo}
        aria-label="Centro de custo analítico"
        list={lista}
        value={texto}
        onChange={(e) => aoDigitar(e.target.value)}
        placeholder="Caminho ou nome do centro"
        className="h-full w-full bg-card px-2 text-small text-foreground outline-none"
      />
      <datalist id={lista}>
        {centros.map((c) => (
          <option key={c.id} value={rotuloCentro(c)}>
            {c.codigo}
          </option>
        ))}
      </datalist>
    </>
  );
});

/** Etiqueta pequena (codigo do item, caminho do centro). */
export function Etiqueta({ children, title }: { children: React.ReactNode; title?: string }) {
  return (
    <span title={title} className="tabular shrink-0 rounded-xs bg-line-soft px-1.5 text-caption leading-5 text-ink-body">
      {children}
    </span>
  );
}

/** Chip de alerta/aviso: tom de atencao (laranja) ou de erro (vermelho). */
export function Chip({ children, tom = 'aviso', testid, titulo, className }: { children: React.ReactNode; tom?: 'aviso' | 'erro'; testid?: string; titulo?: string; className?: string }) {
  return (
    <span
      data-testid={testid}
      title={titulo}
      className={cn('rounded-full px-2 text-caption leading-5 font-medium', tom === 'erro' ? 'bg-destructive/10 text-danger-ink' : 'bg-warning-bg text-warning-ink', className)}
    >
      {children}
    </span>
  );
}

/**
 * Atualiza as celulas (custo, alertas, contexto) SEM tocar nas linhas em edicao: o refresh forcado derrubava o editor aberto quando a
 * simulacao da API voltava no meio da digitacao (o texto digitado se perdia). A linha em edicao se atualiza na simulacao seguinte.
 */
export function atualizarSemEditar(api: { getEditingCells: () => Array<{ rowIndex: number }>; forEachNode: (f: (n: IRowNode) => void) => void; refreshCells: (p: RefreshCellsParams) => void } | null | undefined, columns?: string[]): void {
  if (!api) return;
  const editando = new Set(api.getEditingCells().map((c) => c.rowIndex));
  const base = columns ? { force: true, columns } : { force: true };
  if (editando.size === 0) {
    api.refreshCells(base);
    return;
  }
  const nos: IRowNode[] = [];
  api.forEachNode((n) => {
    if (n.rowIndex === null || !editando.has(n.rowIndex)) nos.push(n);
  });
  api.refreshCells({ ...base, rowNodes: nos });
}
